
import { LikeButton } from '../../../components/engagement/LikeButton';
import { useNavigate } from 'react-router-dom';

interface Track {
  id: string;
  title: string;
  duration: number;
  plays: number;
  previewUrl: string;
  likes: number;
  releaseSlug: string;
}

interface TrackHighlightsProps {
  tracks: Track[];
  currentPreview: {
    track: Track;
    isPlaying: boolean;
    currentTime: number;
  } | null;
  onPreview: (track: Track) => void;
  onLikeCountChange?: (trackId: string, count: number) => void;
}

export default function TrackHighlights({ tracks, currentPreview, onPreview, onLikeCountChange }: TrackHighlightsProps) {
  const navigate = useNavigate();

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPlays = (plays: number) => {
    if (plays >= 1000) {
      return `${(plays / 1000).toFixed(1)}k`;
    }
    return plays.toString();
  };

  return (
    <section className="bg-white rounded-lg border border-neutral-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Popular Right Now</h2>
        <p className="text-sm text-gray-600">
          Most played tracks from this artist
        </p>
      </div>

      <div className="space-y-3">
        {tracks.map((track, index) => {
          const isCurrentTrack = currentPreview?.track.id === track.id;
          const isPlaying = isCurrentTrack && currentPreview.isPlaying;

          return (
            <div
              key={track.id}
              className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer"
              onClick={() => {
                if (track.releaseSlug) {
                  navigate(`/releases/${track.releaseSlug}`);
                }
              }}
            >
              {/* Play Button */}
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onPreview(track);
                }}
                className="w-10 h-10 bg-emerald-100 hover:bg-emerald-200 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
              >
                {isPlaying ? (
                  <i className="ri-pause-fill text-emerald-600"></i>
                ) : (
                  <i className="ri-play-fill text-emerald-600 ml-0.5"></i>
                )}
              </button>

              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-500">
                    #{index + 1}
                  </span>
                  <h3 className="font-semibold text-gray-900 truncate">
                    {track.title}
                  </h3>
                  {isPlaying && (
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                      <div className="w-1 h-4 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-1 h-2 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>{formatPlays(track.plays)} plays</span>
                  <span>{formatDuration(track.duration)}</span>
                </div>
              </div>

              {/* More Actions */}
              <div className="flex items-center gap-2">
                <div onClick={(event) => event.stopPropagation()}>
                  <LikeButton
                    subjectType="track"
                    subjectId={Number(track.id)}
                    initialCount={track.likes}
                    variant="badge"
                    label="Like"
                    size="sm"
                    onCountChange={(next) => onLikeCountChange?.(track.id, next)}
                  />
                </div>
                <button
                  onClick={(event) => event.stopPropagation()}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <i className="ri-more-line text-gray-600"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {tracks.length === 0 && (
        <div className="text-center py-8">
          <i className="ri-music-2-line text-3xl text-gray-400 mb-3"></i>
          <p className="text-gray-600">No tracks available for preview</p>
        </div>
      )}
    </section>
  );
}
